/**
 * True for Basecamp message board / inbox forward markdown (not todo work specs).
 */
export function isBasecampMessageDocument(
  filePath?: string,
  frontmatter?: Record<string, unknown> | null,
): boolean {
  if (filePath?.includes('/messages/') || filePath?.includes('/inbox_forwards/')) {
    return true
  }
  const t = frontmatter?.type
  return t === 'message' || t === 'inbox_forward'
}

/**
 * Derive Basecamp todo_id from a FlowNotes file path when frontmatter is missing.
 * Matches: .../todos/{todoId}-{slug}/... or .../todos/{todoId}-{slug}.md
 *
 * Nested todo folders (a todo escalated into sub-todos, e.g.
 * `.../todos/{hubId}-hub-slug/todos/{childId}-child-slug.md`) contain more than one
 * `/todos/{digits}-` segment. The LAST (rightmost) match - the one closest to the file -
 * is always the document's own todo id; earlier matches belong to ancestor hub folders.
 * Using the first match here previously caused nested documents to resolve to their
 * parent folder's todo id instead of their own.
 *
 * @see docs/implementations/2026-03-17-basecamp-todo-linkage-handoff.md
 * @see docs/product/incidents/2026-07-07-view-on-basecamp-nested-todo-wrong-url.md
 */
export function extractTodoIdFromPath(filePath: string | undefined): string | null {
  if (!filePath) return null
  const matches = [...filePath.matchAll(/\/todos\/(\d+)-/g)]
  if (matches.length === 0) return null
  return matches[matches.length - 1][1]
}

/**
 * Basecamp recording ids are 6+ digits. Reject date prefixes such as `2026`
 * from YYYY-MM-DD product-doc filenames.
 */
export function asBasecampRecordingId(raw: unknown): string | null {
  if (raw == null || raw === '') return null
  const s = String(raw).trim()
  return /^\d{6,}$/.test(s) ? s : null
}

function todoIdFromFrontmatterFields(
  frontmatter?: Record<string, unknown> | null,
): string | null {
  if (!frontmatter) return null
  return asBasecampRecordingId(
    frontmatter.todo_id ?? frontmatter.basecamp_todo_id ?? frontmatter.basecamp_id,
  )
}

/**
 * Command palette Update-vs-Ingest id: frontmatter todo_id first, then path.
 * Does not require the file to live under `/todos/` - a misfiled spec with
 * `todo_id` in YAML still counts as the existing work spec.
 */
export function paletteTodoIdFromDocument(doc: {
  filePath?: string
  frontmatter?: Record<string, unknown> | null
}): string | null {
  const fromFm = todoIdFromFrontmatterFields(doc.frontmatter)
  if (fromFm) return fromFm
  const fromPath = extractTodoIdFromPath(doc.filePath)
  if (fromPath) return fromPath
  if (!doc.filePath) return null
  for (const segment of doc.filePath.split('/')) {
    const match = segment.match(/^(\d{6,})(?:-|\.|$)/)
    if (match) return match[1]
  }
  return null
}

export function documentMatchesPaletteTodoId(
  todoId: string | null | undefined,
  doc: { filePath?: string; frontmatter?: Record<string, unknown> | null },
): boolean {
  const wanted = asBasecampRecordingId(todoId)
  if (!wanted) return false
  return paletteTodoIdFromDocument(doc) === wanted
}

const BASECAMP_TODO_OR_LIST_URL_RE =
  /basecamp\.com\/\d+\/(?:buckets|projects)\/\d+\/(?:todos|todolists|card_tables\/cards)\/(\d+)/i

/** Todo/todolist recording id from a Basecamp web URL, if present. */
export function extractTodoIdFromBasecampUrl(url: unknown): string | null {
  if (typeof url !== 'string' || !url.trim()) return null
  const match = url.match(BASECAMP_TODO_OR_LIST_URL_RE)
  return match ? asBasecampRecordingId(match[1]) : null
}

/**
 * Todo id for Basecamp linkage (View todo, todo-documents, HTML generation).
 * When the path matches `.../todos/{folderTodoId}-{slug}/...`, that folder id wins over
 * frontmatter (satellite files often store a sub-todo id in todo_id).
 */
export function resolveLinkageTodoId(
  filePath: string | undefined,
  frontmatter?: Record<string, unknown> | null
): string | null {
  if (isBasecampMessageDocument(filePath, frontmatter)) return null
  const pathId = asBasecampRecordingId(extractTodoIdFromPath(filePath))
  if (pathId) return pathId
  const fm = frontmatter
  if (!fm) return null
  const raw = fm.todo_id ?? fm.basecamp_todo_id ?? fm.basecamp_id ?? fm.todolist_id
  const fromFields = asBasecampRecordingId(raw)
  if (fromFields) return fromFields
  return extractTodoIdFromBasecampUrl(fm.source_url ?? fm.url)
}

/**
 * True when this document is a Basecamp todo/todolist work spec (path, frontmatter, or URL).
 * Product bets and other notes that merely mention Basecamp are not linked.
 */
export function isBasecampLinkedDocument(
  filePath?: string | null,
  frontmatter?: Record<string, unknown> | null,
  sourceUrl?: string | null,
): boolean {
  if (resolveLinkageTodoId(filePath ?? undefined, frontmatter)) return true
  return extractTodoIdFromBasecampUrl(sourceUrl) != null
}

/** Infer todo vs todolist from README or index doc when browsing `todos/{id}-slug/` folders. */
export function inferBasecampRecordTypeFromDocument(
  basecampId: string,
  doc: {
    filePath?: string
    excerpt?: string
    frontmatter?: Record<string, unknown> | null
  },
): 'todo' | 'todolist' {
  const fm = doc.frontmatter || {}
  if (fm.type === 'todolist' || fm.original_type === 'todolist') return 'todolist'
  const text = `${doc.excerpt || ''}\n${String(fm.url || '')}\n${String(fm.source_url || '')}`
  if (new RegExp(`/todolists/${basecampId}(?:[^0-9]|$)`).test(text)) return 'todolist'
  // Escalated todo folders share the same path shape as todolist hubs; prefer explicit todo linkage.
  if (new RegExp(`/todos/${basecampId}(?:[^0-9]|$)`).test(text)) return 'todo'
  if (String(fm.basecamp_todo_id) === String(basecampId)) return 'todo'
  if (String(fm.todo_id) === String(basecampId)) return 'todo'
  // Escalated todo folders and genuine todolist hubs share `todos/{id}-slug/`.
  // Path + README basename alone is NOT a todolist signal - that mislabeled
  // escalated todos (e.g. Speedy 9884150565) when todo-documents omitted
  // frontmatter and the excerpt lacked a Basecamp URL. Require a positive
  // todolist marker (type, /todolists/ URL above, or todolist_id === id).
  if (String(fm.todolist_id) === String(basecampId) && !fm.todo_id && !fm.basecamp_todo_id) {
    return 'todolist'
  }
  return 'todo'
}
